import { useMemo, useCallback } from 'react';
import moment from 'moment';

export function useCalendarMetrics({ events, currentDate, currentView, enablePeople }) {
  const isOutlookSyncEvent = useCallback(() => false, []);
  const isInCurrentView = useCallback((event) => {
    if (currentView === 'work_week') {
      const weekStart = moment(currentDate).startOf('week').add(0, 'days'); // Monday
      const weekEnd = moment(currentDate).startOf('week').add(4, 'days').endOf('day'); // Friday
      return moment(event.start).isBetween(weekStart, weekEnd, null, '[]');
    }

    const viewStart = moment(currentDate).startOf(
      currentView === 'month'
        ? 'month'
        : currentView === 'week'
        ? 'week'
        : 'day'
    );
    const viewEnd = moment(currentDate).endOf(
      currentView === 'month'
        ? 'month'
        : currentView === 'week'
        ? 'week'
        : 'day'
    );
    return moment(event.start).isBetween(viewStart, viewEnd, null, '[]');
  }, [currentDate, currentView]);

  const totalsCurrentView = useMemo(() => {
    let total = 0;

    const sourceEvents = enablePeople
      ? events.filter(ev => !ev.isPeople)
      : events;

    sourceEvents.forEach(event => {
      if (isInCurrentView(event) && !isOutlookSyncEvent(event)) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60);
        if (duration < 0) return;
        total += duration;
      }
    });

    return { total };
  }, [events, enablePeople, isInCurrentView, isOutlookSyncEvent]);

  const totalsCurrentViewPeople = useMemo(() => {
    let total = 0;

    events.forEach(ev => {
      if (ev.isPeople && isInCurrentView(ev)) {
        const duration = (new Date(ev.end) - new Date(ev.start)) / (1000 * 60 * 60);
        if (duration > 0) total += duration;
      }
    });

    return { total };
  }, [events, isInCurrentView]);

  const totalsYTD = useMemo(() => {
    let total = 0;
    const startOfYear = moment().startOf('year');

    const sourceEvents = enablePeople
      ? events.filter(ev => !ev.isPeople)
      : events;

    sourceEvents.forEach(event => {
      if (moment(event.start).isSameOrAfter(startOfYear) && !isOutlookSyncEvent(event)) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60);
        total += duration;
      }
    });

    return { total };
  }, [events, enablePeople, isOutlookSyncEvent]);

  const totalsYTDPeople = useMemo(() => {
    let total = 0;
    const startOfYear = moment().startOf('year');

    events.forEach(ev => {
      if (ev.isPeople && moment(ev.start).isSameOrAfter(startOfYear)) {
        const duration = (new Date(ev.end) - new Date(ev.start)) / (1000 * 60 * 60);
        if (duration > 0) total += duration;
      }
    });

    return { total };
  }, [events]);

  const getDailyTotals = useCallback((date) => {
    const dayStart = moment(date).startOf('day');
    const dayEnd = moment(date).endOf('day');

    let total = 0;
    events.forEach(event => {
      if (moment(event.start).isBetween(dayStart, dayEnd, null, '[]') && !isOutlookSyncEvent(event)) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60);
        total += duration;
      }
    });
    return { total };
  }, [events, isOutlookSyncEvent]);

  const getCustomPeriodTotals = useCallback((startDate, endDate, context = 'projects') => {
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).endOf('day');

    const sourceEvents = context === 'people'
      ? events.filter(ev => ev.isPeople)
      : (enablePeople ? events.filter(ev => !ev.isPeople) : events);

    let total = 0;

    sourceEvents.forEach(ev => {
      if (isOutlookSyncEvent(ev)) return;

      const evStart = moment(ev.start);
      const evEnd = moment(ev.end);
      if (evEnd.isBefore(start) || evStart.isAfter(end)) return;

      const s = moment.max(evStart, start);
      const e = moment.min(evEnd, end);
      const duration = e.diff(s, 'minutes') / 60;

      if (duration > 0) total += duration;
    });

    return { total };
  }, [events, enablePeople, isOutlookSyncEvent]);

  const getYearOverYearComparison = useCallback((startDate, endDate, context = 'projects') => {
    const currentPeriod = getCustomPeriodTotals(startDate, endDate, context);
    const prevYearStart = moment(startDate).subtract(1, 'year');
    const prevYearEnd = moment(endDate).subtract(1, 'year');
    const previousYearPeriod = getCustomPeriodTotals(
      prevYearStart.format('YYYY-MM-DD'),
      prevYearEnd.format('YYYY-MM-DD'),
      context
    );

    const totalDiff = currentPeriod.total - previousYearPeriod.total;
    const totalPercentChange = previousYearPeriod.total > 0
      ? ((totalDiff / previousYearPeriod.total) * 100)
      : (currentPeriod.total > 0 ? 100 : 0);

    return {
      current: currentPeriod,
      previous: previousYearPeriod,
      differences: {
        total: totalDiff,
        totalPercent: totalPercentChange,
      },
      hasData: currentPeriod.total > 0 || previousYearPeriod.total > 0
    };
  }, [getCustomPeriodTotals]);

  return {
    isInCurrentView,
    totalsCurrentView,
    totalsCurrentViewPeople,
    totalsYTD,
    totalsYTDPeople,
    getDailyTotals,
    getCustomPeriodTotals,
    getYearOverYearComparison,
  };
}
